import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { head, isEmpty, pathOr, pick } from 'ramda'
import Input from 'components/atoms/Input'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isNotEmpty, isNotNilOrEmpty } from 'utils/ramda'
import {
  validateAssignmentField,
  validateAssignmentValues,
  validateEmailValue
} from 'features/createBp/ducks/schema'
import PhoneField from 'components/atoms/PhoneInput'
import Select from 'components/atoms/Select'
import NextButton from 'features/createBp/components/atoms/NextButton'
import {
  assignUserToBpRoutine,
  editUserRelationToBpRoutine,
  inviteUserToBpRoutine
} from 'features/accessControl/ducks/actions'
import PersonDetailsBox from 'features/createBp/components/atoms/PersonDetailsBox'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import { ROLE_DESCRIPTION } from 'utils/constants'
import { ROLE_OPTIONS } from 'utils/roleOptions'
import moment from 'moment'
import { toSnakeCase } from 'js-convert-case/lib'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getOwners, getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { clearUsersFoundByEmailRoutine, getUsersByEmailRoutine } from 'features/bpDetails/ducks/actions'
import { accessControlAssignRoutine } from 'features/bpDetails/ducks/actions'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import APIService from 'services/APIService'
import FileTreeSelector from 'features/bpDetails/Components/files/FileTreeSelector'
import AssignOrganisationSection from 'features/accessControl/components/AssignOrganisationSection'

const api = APIService()

const emptyValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  relationType: '',
  expiresAt: ''
}

const SAFETY_ROLES = ['responsible_person', 'principal_accountable_person', 'principal_designer']

const AddAssignmentModal = ({ initialUserType, closeModal, userToEdit, isAnalyticsView, currentPage, isUnitContext = false }) => {
  const bp = useSelector(getSelectedBp)
  const { t } = useTranslation()
  const [formVisible, setFormVisible] = useState(false)
  const [autoFilled, setAutoFilled] = useState(false)
  const [addedSelf, setAddedSelf] = useState(false)
  const [email, setEmail] = useState('')
  const [hasValidEmail, setHasValidEmail] = useState(false)
  const [valid, setValid] = useState(false)
  const dispatch = useDispatch()
  const foundOwners = useSelector(getOwners)
  const [phoneValid, setPhoneValid] = useState(false)
  const [values, setValues] = useState(
    userToEdit
      ? userToEdit
      : emptyValues
  )
  const [holderKind, setHolderKind] = useState('person')

  const currentUser = useSelector(getCurrentUser)

  const [showFilePermissions, setShowFilePermissions] = useState(false)
  const [fileTree, setFileTree] = useState([])
  const [fileTreeLoading, setFileTreeLoading] = useState(false)
  const [loadingCategoryIds, setLoadingCategoryIds] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const [selectAllLoading, setSelectAllLoading] = useState(false)

  const normalizeSummaryCategory = useCallback((category) => ({
    ...category,
    fileTypes: category.fileTypes || category.file_types,
    file_types: category.fileTypes || category.file_types,
    is_loaded: false
  }), [])

  const mergeCategoryIntoTree = useCallback((incomingCategory) => {
    setFileTree(prev => {
      const nextTree = [...prev]
      const existingIndex = nextTree.findIndex(category => category.id === incomingCategory.id)
      const existingCategory = existingIndex >= 0 ? nextTree[existingIndex] : null

      if (existingIndex >= 0) {
        nextTree[existingIndex] = {
          ...existingCategory,
          ...incomingCategory,
          fileTypes: incomingCategory.fileTypes || incomingCategory.file_types,
          file_types: incomingCategory.fileTypes || incomingCategory.file_types,
          is_loaded: true
        }
      } else {
        nextTree.push({
          ...incomingCategory,
          fileTypes: incomingCategory.fileTypes || incomingCategory.file_types,
          file_types: incomingCategory.fileTypes || incomingCategory.file_types,
          is_loaded: true
        })
      }

      return nextTree
    })
  }, [])

  const fetchFileTreeSummary = useCallback(async () => {
    setFileTreeLoading(true)

    try {
      const res = await api.get(`/building-passport/${bp.id}/files-tree?include_files=0`)
      const data = res?.data?.data || res?.data || []
      const normalizedData = (Array.isArray(data) ? data : Object.values(data)).map(normalizeSummaryCategory)
      setFileTree(normalizedData)
    } catch (err) {
      console.error('Failed to fetch file tree:', err)
    } finally {
      setFileTreeLoading(false)
    }
  }, [bp.id, normalizeSummaryCategory])

  const fetchCategoryFiles = useCallback(async (categoryId) => {
    const targetCategory = fileTree.find(category => category.id === categoryId)
    if (!targetCategory || targetCategory.is_loaded || loadingCategoryIds.includes(categoryId)) {
      return targetCategory
    }

    setLoadingCategoryIds(prev => [...prev, categoryId])

    try {
      const res = await api.get(`/building-passport/${bp.id}/files-tree?category_id=${categoryId}`)
      const data = res?.data?.data || res?.data || []
      const category = Array.isArray(data) ? data[0] : Object.values(data)[0]

      if (category) {
        mergeCategoryIntoTree(category)
      }
      return category || targetCategory
    } catch (err) {
      console.error('Failed to fetch category files:', err)
      return targetCategory
    } finally {
      setLoadingCategoryIds(prev => prev.filter(id => id !== categoryId))
    }
  }, [bp.id, fileTree, loadingCategoryIds, mergeCategoryIntoTree])

  const fetchCategoryFilesBatch = useCallback(async (categoryIds) => {
    const targetCategoryIds = categoryIds.filter(categoryId => {
      const category = fileTree.find(item => item.id === categoryId)
      return category && category.is_loaded !== true && !loadingCategoryIds.includes(categoryId)
    })

    if (targetCategoryIds.length === 0) {
      return fileTree.filter(category => categoryIds.includes(category.id))
    }

    setLoadingCategoryIds(prev => Array.from(new Set([...prev, ...targetCategoryIds])))

    try {
      const res = await api.get(`/building-passport/${bp.id}/files-tree?category_ids=${targetCategoryIds.join(',')}`)
      const data = res?.data?.data || []
      const categories = Array.isArray(data) ? data : Object.values(data)

      categories.forEach(category => {
        mergeCategoryIntoTree(category)
      })

      return categories
    } catch (err) {
      console.error('Failed to batch fetch category files:', err)
      return []
    } finally {
      setLoadingCategoryIds(prev => prev.filter(id => !targetCategoryIds.includes(id)))
    }
  }, [bp.id, fileTree, loadingCategoryIds, mergeCategoryIntoTree])

  const getEditableFileIds = useCallback((categories) => {
    const fileIds = []

    categories.forEach(category => {
      Object.values(category.fileTypes || category.file_types || {}).forEach(fileType => {
        fileType.files?.forEach(file => {
          if ((file?.is_access_editable ?? file?.isAccessEditable ?? true) === true) {
            fileIds.push(file.id)
          }
        })
      })
    })

    return fileIds
  }, [])

  const loadedEditableFileIds = useMemo(
    () => getEditableFileIds(fileTree.filter(category => category.is_loaded !== false)),
    [fileTree, getEditableFileIds]
  )

  const allFilesSelected = (
    loadedEditableFileIds.length > 0 &&
    fileTree.every(category => category.is_loaded !== false) &&
    loadedEditableFileIds.every(fileId => selectedFileIds.includes(fileId))
  )

  const handleSelectDeselectAllFiles = useCallback(async () => {
    if (allFilesSelected) {
      setSelectedFileIds([])
      return
    }

    setSelectAllLoading(true)

    try {
      const unloadedCategoryIds = fileTree
        .filter(category => category.is_loaded === false)
        .map(category => category.id)
      const loadedCategories = fileTree.filter(category => category.is_loaded !== false)
      const fetchedCategories = await fetchCategoryFilesBatch(unloadedCategoryIds)
      const categoriesById = new Map()
      const allLoadedCategories = [...loadedCategories, ...fetchedCategories]

      allLoadedCategories.forEach(category => {
        categoriesById.set(category.id, category)
      })

      setSelectedFileIds(Array.from(new Set(getEditableFileIds(Array.from(categoriesById.values())))))
    } finally {
      setSelectAllLoading(false)
    }
  }, [allFilesSelected, fetchCategoryFilesBatch, fileTree, getEditableFileIds])

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  // Add yourself to this building directly — you won't be in the searchable
  // list if you're a staff user, so fill your own details and treat it as an
  // existing-user assignment (you only need to pick a role).
  const isMeFilled = addedSelf && values.id === currentUser?.id
  const addMe = () => {
    if (!currentUser?.id) return
    setAddedSelf(true)
    setValues(prev => ({
      ...prev,
      id: currentUser.id,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    }))
    setEmail(currentUser.email || '')
    setAutoFilled(true)
    setFormVisible(true)
  }

  const handleModalClose = () => {
    closeModal()
    setAutoFilled(false)
    setAddedSelf(false)
    dispatch(clearUsersFoundByEmailRoutine())
    setValues(emptyValues)
    setFormVisible(false)
  }

  const handleSubmit = () => {
    if (isNotNilOrEmpty(userToEdit)) {
      const relationId =
        pathOr(null, ['pivot', 'id'], userToEdit) ||
        pathOr(null, ['pivot', 0, 'id'], userToEdit)

      dispatch(editUserRelationToBpRoutine({
        bpId: bp.id,
        relationId,
        body: {
          ...(pathOr('', ['relationType'], userToEdit) === values.relationType
            ? {}
            : { relationType: values.relationType }),
          expiresAt: values.expiresAt,
          ...(selectedFileIds.length > 0 ? { filePermissions: selectedFileIds } : {})
        }
      }))
    } else {
      if (isUnitContext) {
        if (autoFilled) {
          dispatch(assignUserToBpRoutine({
            bpId: bp.id,
            body: {
              userId: values.id,
              relationType: values.relationType,
              expiresAt: values.expiresAt,
              ...(selectedFileIds.length > 0 ? { filePermissions: selectedFileIds } : {})
            },
            isEdit: true,
            isAnalyticsView,
            currentPage
          }))
        } else {
          dispatch(inviteUserToBpRoutine({
            bpId: bp.id,
            body: {
              email: values.email.toLowerCase(),
              relationType: values.relationType,
              ...pick(['firstName', 'lastName', 'phoneNumber', 'expiresAt'], values),
              ...(selectedFileIds.length > 0 ? { filePermissions: selectedFileIds } : {})
            },
            isEdit: true,
            isDraft: bp.status === 'draft'
          }))
        }
      } else {
        if (autoFilled) {
          dispatch(accessControlAssignRoutine.trigger({
            bpId: bp.id,
            relation_type: values.relationType,
            user_id: values.id,
            expires_at: values.expiresAt || null,
            note: values.note || null,
            ...(selectedFileIds.length > 0 ? { filePermissions: selectedFileIds } : {})
          }))
        } else {
          dispatch(accessControlAssignRoutine.trigger({
            bpId: bp.id,
            relation_type: values.relationType,
            email: (values.email || '').toLowerCase(),
            first_name: values.firstName || '',
            last_name: values.lastName || '',
            phone_number: values.phoneNumber || '',
            expires_at: values.expiresAt || null,
            note: values.note || null,
            ...(selectedFileIds.length > 0 ? { filePermissions: selectedFileIds } : {})
          }))
        }
      }
    }

    handleModalClose()
  }

  const searchUser = useCallback(() => {
    setFormVisible(true)
    dispatch(getUsersByEmailRoutine({ email, type: 'owner' }))
  }, [dispatch, email])

  useEffect(() => {
    validateEmailValue({ email }, setHasValidEmail)
    validateAssignmentValues(values, setValid)
  }, [values, email])

  const handleChangeEmail = (name, value) => {
    setAddedSelf(false)
    handleValueChange(name, value)
    setEmail(value)
  }

  useEffect(() => {
    if (isNotNilOrEmpty(userToEdit)) {
      setValues({
        ...userToEdit,
        expiresAt: pathOr('', ['pivot', 'expiresAt'], userToEdit)
      })
      setEmail(userToEdit.email)
    } else {
      setValues({
        ...emptyValues,
        relationType: initialUserType ? initialUserType : ''
      })
    }
  }, [userToEdit, initialUserType])

  useEffect(() => {
    // Don't clobber the details we just filled in via "Add me".
    if (addedSelf) return
    if (isNotNilOrEmpty(foundOwners)) {
      const foundUser = head(foundOwners)
      setValues(prev => ({
        ...prev,
        id: foundUser.id,
        ...foundUser,
        email
      }))
      setAutoFilled(true)
    } else {
      setValues(prev => ({
        ...prev,
        relationType: prev.relationType,
        expiresAt: prev.expiresAt,
        email
      }))
      setAutoFilled(false)
    }
  }, [foundOwners, email, addedSelf])

  useEffect(() => {
    isNotEmpty(email) && setFormVisible(true)
  }, [])

  useEffect(() => {
    fetchFileTreeSummary()
  }, [fetchFileTreeSummary])

  // --- nou: logică pentru enable/disable pe Save ---
  const isEditing = isNotNilOrEmpty(userToEdit)
  const isSafetyRole = SAFETY_ROLES.includes(values.relationType)
  const showOrgSection = isSafetyRole && holderKind === 'organisation' && !isEditing

  const canSubmit = (() => {
    // Editare relație existentă – nu cerem email/telefon, doar schema validă + role
    if (isEditing) {
      return valid && !!values.relationType
    }

    // User găsit automat – nu mai obligăm completarea nume/telefon manual
    if (autoFilled) {
      return valid && hasValidEmail && !!values.relationType
    }

    // Invitație nouă:
    //  - au dat "check email" (formVisible)
    //  - email valid
    //  - schema validă (role + câmpuri obligatorii)
    //  - telefon valid
    return (
      formVisible &&
      hasValidEmail &&
      phoneValid &&
      valid &&
      !!values.relationType
    )
  })()
  // --- end nou ---

  return (
    <ContentWrapper>
      {!isEditing && currentUser?.id && !isMeFilled && !showOrgSection && (
        <AddMeButton type='button' onClick={addMe}>
          + Add me to this building
        </AddMeButton>
      )}
      <Select
        label='Role'
        noMargin={isNotNilOrEmpty(values.relationType)}
        onChange={handleValueChange}
        validate={validateAssignmentField(values)}
        placeholder='Select'
        value={toSnakeCase(values.relationType)}
        name='relationType'
        options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))}
      />
      {
        values.relationType && (
          <DescriptionWrapper>
            <InputDescription>
              {ROLE_DESCRIPTION[values.relationType]
                .replace('[User name]', `${currentUser.firstName} ${currentUser.lastName}`)}
            </InputDescription>
          </DescriptionWrapper>
        )
      }
      {isSafetyRole && !isEditing && (
        <HolderToggleWrap>
          <ToggleLabel>Who will hold this role?</ToggleLabel>
          <HolderToggle role='tablist' aria-label='Who will hold this role?'>
            <ToggleBtn
              type='button'
              role='tab'
              aria-selected={holderKind === 'person'}
              $active={holderKind === 'person'}
              onClick={() => setHolderKind('person')}
            >
              Person
            </ToggleBtn>
            <ToggleBtn
              type='button'
              role='tab'
              aria-selected={holderKind === 'organisation'}
              $active={holderKind === 'organisation'}
              onClick={() => setHolderKind('organisation')}
            >
              Organisation
            </ToggleBtn>
          </HolderToggle>
        </HolderToggleWrap>
      )}
      {showOrgSection ? (
        <AssignOrganisationSection
          bpId={bp.id}
          role={values.relationType}
          onDone={handleModalClose}
        />
      ) : (
        <>
      <Input
        required
        label='Expires at'
        inputProps={{
          min: moment().format('YYYY-MM-DD')
        }}
        type='date'
        value={moment(values.expiresAt).format('YYYY-MM-DD')}
        optional
        name='expiresAt'
        onChange={handleValueChange}
        additionalLabel={
          <ClearInputButton
            disabled={isEmpty(values.expiresAt)}
            onClick={() => handleValueChange('expiresAt', '')}
          >
            clear
          </ClearInputButton>
        }
      />
      {
        isNotNilOrEmpty(userToEdit)
          ? (
            <PersonDetailsBox
              details={values}
              hideEdit
            />
          )
          : (
            <FormWrapper>
              <Input
                type='email'
                required
                label={t('signUp.labels.email')}
                value={pathOr('', ['email'], values)}
                name='email'
                onChange={handleChangeEmail}
                validate={validateAssignmentField(values)}
              />
              <CheckButtonWrapper>
                <CheckEmailButton disabled={!hasValidEmail} type='button' onClick={searchUser}>
                  check email
                </CheckEmailButton>
              </CheckButtonWrapper>
              {
                formVisible && (
                  <>
                    <SectionName>personal details</SectionName>
                    <Input
                      required
                      label={t('signUp.labels.firstName')}
                      value={pathOr('', ['firstName'], values)}
                      name='firstName'
                      onChange={handleValueChange}
                      validate={validateAssignmentField(values)}
                      disabled={autoFilled}
                    />
                    <Input
                      required
                      label={t('signUp.labels.lastName')}
                      value={pathOr('', ['lastName'], values)}
                      name='lastName'
                      onChange={handleValueChange}
                      validate={validateAssignmentField(values)}
                      disabled={autoFilled}
                    />
                    <PhoneField
                      name='phoneNumber'
                      label='Phone number'
                      value={pathOr('', ['phoneNumber'], values)}
                      onChange={handleValueChange}
                      validate={v => setPhoneValid(v)}
                      disabled={autoFilled}
                      noValidation={autoFilled}
                      optional
                    />
                  </>
                )
              }
            </FormWrapper>
          )
      }
      <FilePermissionsSection>
        <FilePermissionsToggle onClick={() => {
          const next = !showFilePermissions
          setShowFilePermissions(next)
        }}>
          <FolderOutlinedIcon style={{ fontSize: 16 }} />
          File Access (Optional)
          <ToggleArrow $open={showFilePermissions}>&#9656;</ToggleArrow>
        </FilePermissionsToggle>

        {showFilePermissions && (
          <FilePermissionsContent>
            <FilePermissionsActions>
              <SelectAllFilesButton
                type='button'
                disabled={fileTreeLoading || selectAllLoading || fileTree.length === 0}
                onClick={handleSelectDeselectAllFiles}
              >
                {selectAllLoading ? 'Selecting...' : (allFilesSelected ? 'Deselect all' : 'Select all')}
              </SelectAllFilesButton>
            </FilePermissionsActions>
            <FileTreeSelector
              fileTree={fileTree}
              selected={selectedFileIds}
              onSelectionChange={setSelectedFileIds}
              loading={fileTreeLoading}
              loadingCategoryIds={loadingCategoryIds}
              onCategoryExpand={fetchCategoryFiles}
              onCategoriesLoad={fetchCategoryFilesBatch}
              maxHeight='250px'
              compact
            />
          </FilePermissionsContent>
        )}
      </FilePermissionsSection>
      <NextButton
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        Save
      </NextButton>
        </>
      )}
    </ContentWrapper>
  )
}

export default AddAssignmentModal

const AddMeButton = styled.button`
  width: 100%;
  margin-bottom: 14px;
  padding: 10px 14px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  background: #e6f7f9;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.secondary}; color: #fff; }
`

const FormWrapper = styled.form`
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  margin-bottom: 25px;

  @media (max-width: 640px) {
    padding: 12px;
    margin-bottom: 20px;
  }
`

const SectionName = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: .1px;
  margin: 15px 0;

  @media (max-width: 640px) {
    font-size: 11px;
    margin: 12px 0;
  }
`

const CheckEmailButton = styled.button`
  background-color: ${({ theme }) => theme.colors.haiti};
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid transparent;
  transition: all .3s;
  border-radius: 4px;
  padding: 5px 15px;
  white-space: nowrap;

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.haiti};
    border-color: ${({ theme }) => theme.colors.haiti};
  }

  ${({ disabled, theme }) => disabled && css`
    background-color: ${theme.colors.grey[500]};
    color: ${theme.colors.darkGrey};
    border-color: ${theme.colors.grey[900]};
    cursor: default;

    &:hover {
      background-color: ${theme.colors.grey[500]};
      color: ${theme.colors.grey[900]};
      border-color: transparent;
    }
  `}

  @media (max-width: 640px) {
    padding: 8px 16px;
    font-size: 13px;
    width: 100%;
  }
`

const CheckButtonWrapper = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: 640px) {
    width: 100%;
  }
`

const ContentWrapper = styled.div`
  max-width: 420px;
  margin: 0 auto;
  padding: 0 8px;
  position: relative;
  z-index: 1;

  @media (max-width: 640px) {
    max-width: 100%;
    padding: 0 4px;
  }

  & > div {
    position: relative;
    z-index: 1;
  }
`

const DescriptionWrapper = styled.div`
  margin: 5px 0 15px;

  @media (max-width: 640px) {
    margin: 4px 0 12px;
  }
`

const ClearInputButton = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: uppercase;
  cursor: pointer;
  font-size: 12px;

  ${({ disabled }) => disabled && css`
    color: ${({ theme }) => theme.colors.grey[600]};
    cursor: default;
  `}

  @media (max-width: 640px) {
    font-size: 11px;
  }
`

const FilePermissionsSection = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 8px;
  overflow: hidden;
`

const FilePermissionsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f9fafb'};
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#1e293b'};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey || '#e5e7eb'};
  }
`

const ToggleArrow = styled.span`
  margin-left: auto;
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => $open ? '90deg' : '0deg'});
`

const FilePermissionsContent = styled.div`
  padding: 8px;
`

const FilePermissionsActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
`

const SelectAllFilesButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary || '#33becc'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: #94a3b8;
    cursor: default;
  }
`

const HolderToggleWrap = styled.div`
  margin: 4px 0 18px;
`

const ToggleLabel = styled.div`
  font-weight: bold;
  font-size: 12px;
  letter-spacing: 0.11px;
  margin-bottom: 7px;
`

const HolderToggle = styled.div`
  display: inline-flex;
  gap: 6px;
`

const ToggleBtn = styled.button`
  min-height: 40px;
  padding: 8px 18px;
  border-radius: 999px;
  font: inherit;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.white : theme.colors.secondary)};
  transition: background 0.15s ease, color 0.15s ease;

  @media (max-width: 640px) {
    padding: 8px 14px;
  }
`
